import React, { useState } from 'react';

import { ArrowRightIcon } from '../../../../../../../../components/Icons/ArrowRightIcon';
import NextImage, { StaticImageData } from 'next/image';
import {
  MapIcon,
  MapIconDefaultScale,
  MapIconName,
  MapIconScale,
} from '../../../../../../../../components/Maps/components/MapIcon';
import { MapStyle } from '../../../../../../../../components/Maps/constants/constants';
import { Modal } from '../../../../../../../../components/Modal/Modal';
import { Text } from '../../../../../../../../components/Text/Text';
import { ToggleSwitch } from '../../../../../../../../components/ToggleSwitch/ToggleSwitch';
import { trackEvent } from '../../../../../../../../helpers/analytics/trackEvent';
import { EventMykissExploreLayerToggled } from '../../../../../../../../interfaces/events';
import { IModalContent } from '../../../../../../constants/proModalContent';
import { ProMapLayerModal } from '../../../../../ProMapLayerModal/ProMapLayerModal';

import hybridStyleBackground from './assets/hybrid.png';
import terrainStyleBackground from './assets/terrain.png';

interface IProps {
  mapStyle: MapStyle;
  title: string;
  isActive: boolean;
  onToggle: () => void;
  analyticsEventName: EventMykissExploreLayerToggled['properties']['layer_type'];
  icon?: MapIconName;
  image?: StaticImageData;
  description?: string;
  actionButton?: string;
  onActionButtonClick?: () => void;
  proModalContent?: IModalContent;
  mapIconScale?: MapIconScale;
}

export const MapLayerSettings = ({
  mapStyle,
  icon,
  image,
  title,
  description,
  actionButton,
  onActionButtonClick,
  isActive,
  onToggle,
  analyticsEventName,
  proModalContent,
  mapIconScale = MapIconDefaultScale,
}: IProps) => {
  const [isProModalOpen, setIsProModalOpen] = useState(false);

  const backgroundImage =
    mapStyle === 'hybrid' ? hybridStyleBackground.src : terrainStyleBackground.src;

  const trackToggleEvent = (): void => {
    void trackEvent({
      name: 'mykiss_explore_layer_toggled',
      properties: {
        layer_type: analyticsEventName,
        source: 'drawer_menu',
        state: isActive,
      },
    });
  };

  const proCTA = (): void => {
    setIsProModalOpen(true);
    trackToggleEvent();
  };

  const handleOnToggle = (): void => {
    onToggle();
    trackToggleEvent();
  };

  return (
    <>
      {proModalContent && isProModalOpen && (
        <Modal
          isOpen
          onClose={() => {
            setIsProModalOpen(false);
          }}
          size="xl"
          style={{ minHeight: '580px', maxWidth: '1280px' }}
        >
          <ProMapLayerModal {...proModalContent} />
        </Modal>
      )}
      <div className="flex min-h-[68px] grow">
        <div
          style={{ backgroundImage: `url(${backgroundImage})`, flex: '70px 0 0' }}
          className="flex items-center justify-center rounded-l-md bg-cover"
        >
          {icon && <MapIcon alt={title} name={icon} scale={mapIconScale} mapStyle={mapStyle} />}
          {image && <NextImage alt={title} src={image} unoptimized />}
        </div>
        <div className="bg-sea-foam flex grow items-center p-3">
          <div>
            <Text variant="sm" className="mb-0 font-semibold">
              {title}
            </Text>
            {description && (
              <Text variant="xs" className="mt-1 mb-0">
                {description}
              </Text>
            )}
            {actionButton && onActionButtonClick && (
              <button
                type="button"
                onClick={() => {
                  onActionButtonClick();
                  void trackEvent({
                    name: 'mykiss_explore_drawer_viewed',
                    properties: {
                      type: `subview_${analyticsEventName}`,
                      prev_type: 'mapLayers',
                      is_subview: true,
                    },
                  });
                }}
                className="mt-2"
                data-testid="settingsButton"
              >
                <div className="text-pinktail flex font-semibold">
                  <Text variant="sm" className="mb-0">
                    {actionButton}
                  </Text>
                  <div className="ml-0.5 inline-block">
                    <ArrowRightIcon width={20} height={20} />
                  </div>
                </div>
              </button>
            )}
          </div>
        </div>
        <div className="bg-sea-foam flex items-center rounded-r-md p-3 pl-0">
          <ToggleSwitch
            isActive={isActive}
            onClick={handleOnToggle}
            proOptions={proModalContent ? { proOnly: true, proCTA } : undefined}
          />
        </div>
      </div>
    </>
  );
};
